import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LOGO_URL } from "@/lib/content";

const sections = [
  {
    n: "1",
    title: "Who We Are",
    body: [
      "Swingle is a software platform that enables venues and event organisers (\"Venue\") to sell tickets online.",
      "Swingle is not the organiser, promoter or operator of events listed on the platform unless expressly stated otherwise.",
      "The contract for attending an event is between you and the Venue organising the event.",
    ],
  },
  {
    n: "2",
    title: "Swingle's Role",
    body: [
      "Swingle provides the technology used to advertise events, process bookings and facilitate payments.",
      "Unless expressly stated otherwise:",
      {
        list: [
          "Swingle does not organise or operate events.",
          "Swingle is not responsible for the management, safety, security or operation of any event.",
          "Swingle acts solely as the Venue's technology and payment platform.",
          "The Venue remains responsible for delivering the event and complying with all applicable laws and licensing requirements.",
        ],
      },
      "Nothing in these Terms excludes Swingle's liability where it cannot legally be excluded.",
    ],
  },
  {
    n: "3",
    title: "Purchasing Tickets",
    body: [
      "When you place an order, you are making an offer to purchase tickets for the selected event.",
      "Your booking is confirmed once:",
      {
        list: [
          "payment has been successfully authorised; and",
          "you receive your booking confirmation by email or through the Swingle platform.",
        ],
      },
      "All ticket purchases are subject to availability.",
      "You are responsible for ensuring that all information provided during checkout is accurate.",
    ],
  },
  {
    n: "4",
    title: "Prices",
    body: [
      "Ticket prices are displayed before payment.",
      "Where applicable, prices include VAT.",
      "Any booking fees, service charges or other charges will be clearly displayed before you complete your purchase.",
      "Prices may change before purchase but will not change once your booking has been confirmed.",
    ],
  },
  {
    n: "5",
    title: "Payments",
    body: [
      "Payments are processed securely using an authorised third-party payment processor.",
      "Swingle does not store your payment card details.",
      "Although payments are processed through the Swingle platform, funds are collected on behalf of the Venue before being settled in accordance with Swingle's agreement with the Venue.",
    ],
  },
  {
    n: "6",
    title: "Electronic Tickets",
    body: [
      "Tickets are normally delivered electronically.",
      "Each ticket contains a unique barcode or QR code.",
      "Tickets must not be copied, altered or duplicated.",
      "Your email address is used for ticket delivery and the venue and/or Swingle may contact you from time to time with relevant offers or services.",
      "You can unsubscribe at any time.",
      "The Venue may refuse entry if:",
      {
        list: [
          "a ticket has already been used;",
          "a ticket has been copied or altered;",
          "a ticket has been obtained fraudulently; or",
          "the purchaser cannot provide reasonable proof of purchase if requested.",
        ],
      },
    ],
  },
  {
    n: "7",
    title: "Age Restrictions",
    body: [
      "Some events are restricted to customers aged 18 or over or another minimum age specified by the Venue.",
      "By purchasing a ticket, you confirm that you and anyone attending using your booking meet the stated age requirement.",
      "The Venue may request valid photographic identification on arrival.",
      "Acceptable identification includes:",
      {
        list: [
          "Passport",
          "UK Photocard Driving Licence",
          "PASS-approved Proof of Age Card",
        ],
      },
      "Failure to produce valid identification may result in refusal of entry without refund.",
    ],
  },
  {
    n: "8",
    title: "Entry to the Venue",
    body: [
      "Admission remains entirely at the discretion of the Venue and is subject to:",
      {
        list: [
          "venue capacity;",
          "licensing conditions;",
          "security procedures;",
          "venue policies; and",
          "applicable laws.",
        ],
      },
      "Purchasing a ticket does not guarantee entry where admission is lawfully refused.",
    ],
  },
  {
    n: "9",
    title: "Licensing Compliance",
    body: [
      "The Venue must comply with all licensing laws and conditions applicable to the event.",
      "The Venue reserves the right to refuse admission or remove customers where necessary to comply with:",
      {
        list: [
          "the Licensing Act 2003;",
          "licensing conditions;",
          "health and safety requirements;",
          "fire safety regulations;",
          "security procedures; or",
          "lawful instructions issued by Venue management or emergency services.",
        ],
      },
    ],
  },
  {
    n: "10",
    title: "Intoxication and Behaviour",
    body: [
      "The Venue reserves the right to refuse admission or remove any customer who:",
      {
        list: [
          "appears intoxicated by alcohol or drugs;",
          "possesses illegal drugs or prohibited items;",
          "behaves aggressively, abusively or violently;",
          "threatens the safety or enjoyment of others;",
          "damages property; or",
          "refuses reasonable instructions from Venue staff or security personnel.",
        ],
      },
      "Where entry is refused or a customer is removed for these reasons, no refund will be payable unless required by law.",
    ],
  },
  {
    n: "11",
    title: "Changes to Events",
    body: [
      "Occasionally it may be necessary for the Venue to make reasonable changes to an event.",
      "Examples include changes to:",
      {
        list: [
          "opening times;",
          "performers or DJs;",
          "room allocation;",
          "running order;",
          "supporting acts; or",
          "operational arrangements.",
        ],
      },
      "Minor changes do not normally entitle customers to a refund.",
      "If the changes are significant enough to materially alter the event, customers may be entitled to an appropriate remedy under applicable consumer law.",
    ],
  },
  {
    n: "12",
    title: "Event Cancellation",
    body: [
      "If an event is cancelled and not rescheduled, customers will normally receive a refund of the ticket price paid.",
      "Refunds will usually be made to the original payment method.",
      "Unless required by law, booking fees or service charges relating to services already provided may not be refundable.",
      "Neither Swingle nor the Venue is responsible for indirect costs including:",
      {
        list: [
          "travel;",
          "accommodation;",
          "parking;",
          "childcare;",
          "loss of earnings; or",
          "any other consequential expenses.",
        ],
      },
    ],
  },
  {
    n: "13",
    title: "Rescheduled Events",
    body: [
      "If an event is postponed or rescheduled, tickets will usually remain valid for the new date.",
      "If you cannot attend the new date, the Venue's refund or exchange policy will apply.",
      "Where consumer legislation requires a refund because the event has changed significantly, the appropriate remedy will be provided.",
    ],
  },
  {
    n: "14",
    title: "Customer Cancellations and Change of Mind",
    body: [
      "Under the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013, tickets for leisure events taking place on a specific date are generally exempt from the statutory 14-day cooling-off period.",
      "This means that once your booking has been confirmed, you cannot cancel your purchase simply because:",
      {
        list: [
          "you have changed your mind;",
          "you are no longer able to attend; or",
          "you purchased tickets in error.",
        ],
      },
      "Nothing in this section affects your legal rights where the event is cancelled, significantly changed or where a refund is otherwise required by law.",
    ],
  },
  {
    n: "15",
    title: "Ticket Resale",
    body: [
      "Tickets may not be resold for commercial gain without the prior written permission of the Venue.",
      "The Venue reserves the right to cancel any ticket that has been resold in breach of these Terms.",
    ],
  },
  {
    n: "16",
    title: "Chargebacks and Fraud",
    body: [
      "If payment is reversed through a chargeback or payment dispute without valid legal grounds, Swingle and/or the Venue reserve the right to:",
      {
        list: [
          "cancel associated tickets;",
          "refuse entry to the event;",
          "recover any outstanding sums; and",
          "recover reasonable costs incurred as a result of the dispute where legally permitted.",
        ],
      },
      "Fraudulent purchases may also be reported to the relevant authorities.",
    ],
  },
  {
    n: "17",
    title: "Liability",
    body: [
      "Swingle is responsible only for providing the ticketing platform and payment facilitation services.",
      "Swingle is not responsible for:",
      {
        list: [
          "cancellation of events;",
          "changes to event content;",
          "acts or omissions of the Venue;",
          "injuries sustained at events;",
          "theft or loss of personal belongings;",
          "the conduct of other attendees; or",
          "any loss arising from the operation of the event itself.",
        ],
      },
      "The Venue remains responsible for organising and delivering the event.",
      "Nothing in these Terms limits liability for:",
      {
        list: [
          "death or personal injury caused by negligence;",
          "fraud or fraudulent misrepresentation; or",
          "any liability that cannot legally be excluded.",
        ],
      },
    ],
  },
  {
    n: "18",
    title: "Consumer Rights",
    body: [
      "Nothing in these Terms affects your statutory rights.",
      "These Terms are intended to comply with the Consumer Rights Act 2015 and other applicable UK consumer protection legislation.",
      "If any provision of these Terms conflicts with your statutory rights, your statutory rights will prevail.",
    ],
  },
  {
    n: "19",
    title: "Privacy",
    body: [
      "Swingle processes personal information in accordance with its Privacy Policy.",
      "Your information may be shared with the Venue where reasonably necessary to administer your booking and provide the event.",
    ],
  },
  {
    n: "20",
    title: "Force Majeure",
    body: [
      "Neither Swingle nor the Venue shall be liable for delays, cancellations or changes caused by circumstances beyond their reasonable control, including:",
      {
        list: [
          "severe weather;",
          "flooding;",
          "fire;",
          "acts of terrorism;",
          "civil unrest;",
          "industrial action;",
          "government restrictions;",
          "pandemics;",
          "utility failures; or",
          "other unforeseen events.",
        ],
      },
      "Nothing in this section limits any refund or remedy required by law.",
    ],
  },
  {
    n: "21",
    title: "Governing Law",
    body: [
      "These Terms are governed by the laws of England and Wales.",
      "Any dispute arising from these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.",
    ],
  },
];

export default function TicketTerms() {
  return (
    <div className="min-h-screen bg-black text-[#F5F5F0]" data-testid="ticket-terms-page">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="ticket-terms-home-link"
            className="flex items-center gap-3"
          >
            <img src={LOGO_URL} alt="Swingle" className="h-8 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="ticket-terms-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <section className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-6">Legal</p>
          <h1
            data-testid="ticket-terms-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight"
          >
            Ticket Purchase
            <br />
            <span className="italic text-[#D9A6F5]">Terms &amp; Conditions</span>
          </h1>
          <p className="mt-8 text-sm uppercase tracking-[0.18em] text-[#A3A3A3]">
            Last Updated: 14th July 2026
          </p>
          <p className="mt-6 text-sm md:text-base text-[#C9C9C0] leading-relaxed max-w-2xl">
            These Terms &amp; Conditions apply whenever you purchase a ticket through the
            Swingle platform. By purchasing a ticket, you agree to these Terms.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-14">
          {sections.map((s) => (
            <article
              key={s.n}
              data-testid={`ticket-terms-section-${s.n}`}
              className="grid md:grid-cols-[80px_1fr] gap-4 md:gap-10"
            >
              <div className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]/70 pt-1">
                {s.n.padStart(2, "0")}
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-normal text-[#F5F5F0] mb-4">
                  {s.title}
                </h2>
                <div className="space-y-4 text-sm md:text-base text-[#C9C9C0] leading-relaxed">
                  {(() => {
                    let paraCount = 0;
                    return s.body.map((item, i) => {
                      if (typeof item === "string") {
                        paraCount += 1;
                        return (
                          <p key={i} className="flex gap-3">
                            <span className="text-[#D9A6F5]/80 flex-shrink-0 min-w-[38px]">
                              {`${s.n}.${paraCount}`}
                            </span>
                            <span>{item}</span>
                          </p>
                        );
                      }
                      return (
                        <ul key={i} className="list-none space-y-2 pl-0 md:pl-[38px]">
                          {item.list.map((li, j) => (
                            <li key={j} className="flex gap-3">
                              <span className="text-[#D9A6F5] mt-2 inline-block h-px w-3 flex-shrink-0" />
                              <span>{li}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    });
                  })()}
                </div>
              </div>
            </article>
          ))}

          <article
            data-testid="ticket-terms-section-contact"
            className="grid md:grid-cols-[80px_1fr] gap-4 md:gap-10 pt-4 border-t border-white/10"
          >
            <div className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]/70 pt-8">
              Contact
            </div>
            <div className="pt-6 space-y-3 text-sm md:text-base text-[#C9C9C0] leading-relaxed">
              <p>If you have any questions about your ticket purchase, please contact the Venue in the first instance.</p>
              <p>
                For technical issues relating to your booking or the Swingle platform,
                please contact Swingle via the contact details available on{" "}
                <Link to="/" className="text-[#D9A6F5] hover:underline">our website</Link>.
              </p>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
